/**
 * Markdown from the model, a scraped page or an editor, rendered safely.
 *
 * The tools used to turn Markdown into HTML with a chain of `.replace()` calls
 * and hand the result to `dangerouslySetInnerHTML`. Nothing was escaped, so an
 * article that contained `<img src=x onerror=…>` — from a scraped page, or a
 * model echoing one — ran script in the editor's session, next to the MSAL
 * tokens in localStorage. react-markdown never renders raw HTML, and its
 * default URL transform drops `javascript:` and other unsafe link schemes.
 *
 * `prose` (the typography plugin) styles headings, lists and emphasis in both
 * themes. `inline` renders a single sentence (a promo line with a link)
 * without paragraph margins.
 */

import type { ComponentProps } from 'react'
import ReactMarkdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'

import { cn } from './styles'

function Link({ href, children }: ComponentProps<'a'>) {
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-primary underline underline-offset-2 hover:text-primary-hover"
        >
            {children}
        </a>
    )
}

const BLOCK: Components = { a: Link }
// Single line breaks kept inside a paragraph or list item only — on the
// container, whitespace-pre-line also turned the newlines between blocks into
// extra blank lines.
const BLOCK_WITH_BREAKS: Components = {
    a: Link,
    p: ({ children }) => <p className="whitespace-pre-line">{children}</p>,
    li: ({ children }) => <li className="whitespace-pre-line">{children}</li>,
}
const INLINE: Components = { a: Link, p: ({ children }) => <span>{children}</span> }

interface MarkdownProps {
    children: string | null | undefined
    /** One sentence, no block margins. */
    inline?: boolean
    /** Keep single line breaks (text written with Enter, not Markdown). */
    preserveLineBreaks?: boolean
    size?: 'sm' | 'base' | 'lg'
    className?: string
}

const SIZE = { sm: 'prose-sm', base: 'prose-base', lg: 'prose-lg' } as const

// The typography plugin has its own dark palette, reached through the dark variant.
const PROSE = 'prose max-w-none text-fg dark:prose-invert' // design-token-allow

export default function Markdown({ children, inline = false, preserveLineBreaks = false, size = 'base', className }: MarkdownProps) {
    const text = children ?? ''
    if (inline) {
        return (
            <span className={`${preserveLineBreaks ? 'whitespace-pre-line' : ''} ${className ?? ''}`}>
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={INLINE}>
                    {text}
                </ReactMarkdown>
            </span>
        )
    }
    return (
        <div className={cn(PROSE, SIZE[size], className)}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={preserveLineBreaks ? BLOCK_WITH_BREAKS : BLOCK}>
                {text}
            </ReactMarkdown>
        </div>
    )
}
